import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Search, PackageCheck, Undo2, Plus, X, Check, Pause } from 'lucide-react';
import {
  BUYER_REFUND_STATES,
  buyerReturnActionAllowed,
  type BuyerRefundState,
  type BuyerReturn,
  type BuyerReturnAction,
  type BuyerReturnDetail,
  type BuyerReturnSaleLine,
} from '@tcvs/shared';
import { useAuth } from '../auth/AuthContext';
import { hasAnyGroup } from '../auth/cognito';
import { useBuyerReturnsApi } from './buyer-returns-api';
import './buyer-returns.css';

const label = (value: string) =>
  value
    .toLowerCase()
    .replaceAll('_', ' ')
    .replace(/^./, (s) => s.toUpperCase());

function failureMessage(result: { error: string; message?: string }): string {
  const messages: Record<string, string> = {
    card_still_in_inventory_reconcile_original_pick_first:
      'TCVS still records this card in inventory. Reconcile its original pick before opening a return.',
    destination_batch_requires_row_and_box: 'Choose a batch that is in a row and a box.',
    sale_line_must_link_exactly_one_card:
      'This order item must be linked to one physical TCVS card.',
    observed_order_not_found:
      'Order not found in TCVS. Check the order number and marketplace synchronization.',
    return_changed_reload: 'This return changed. Refresh it before submitting another action.',
    return_or_inventory_changed_reload:
      'The return or inventory changed. Refresh the return and check its placement.',
    consigned_sale_requires_allocation_reconciliation:
      'Reconcile the consigned sale before opening its return.',
    original_listing_link_missing_reconcile_first:
      'Reconcile the original eBay listing link before restocking.',
  };
  return messages[result.message ?? result.error] ?? result.message ?? result.error;
}
export function BuyerReturnsPage(): JSX.Element {
  const api = useBuyerReturnsApi();
  const { claims } = useAuth();
  const admin = claims !== null && hasAnyGroup(claims, ['admin']);
  const [items, setItems] = useState<BuyerReturn[]>([]),
    [cursor, setCursor] = useState<string>();
  const [detail, setDetail] = useState<BuyerReturnDetail>();
  const [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [loaded, setLoaded] = useState(false);
  const lock = useRef(false),
    request = useRef(0);
  const [showNew, setShowNew] = useState(false),
    [channel, setChannel] = useState<'EBAY_US' | 'OTHER'>('EBAY_US');
  const [orderId, setOrderId] = useState(''),
    [lineItemId, setLineItemId] = useState(''),
    [cardId, setCardId] = useState('');
  const [lines, setLines] = useState<BuyerReturnSaleLine[]>([]),
    [reason, setReason] = useState(''),
    [reference, setReference] = useState('');
  const [note, setNote] = useState(''),
    [batchId, setBatchId] = useState(''),
    [refund, setRefund] = useState<BuyerRefundState>('NOT_RECORDED');
  const [confirmed, setConfirmed] = useState(false);

  const load = useCallback(
    async (next?: string) => {
      const generation = ++request.current;
      try {
        const result = await api.list(next);
        if (generation !== request.current) return;
        if (!result.ok) {
          setError(failureMessage(result));
          return;
        }
        setItems((old) =>
          next
            ? [
                ...old,
                ...result.data.items.filter((r) => !old.some((o) => o.returnId === r.returnId)),
              ]
            : result.data.items,
        );
        setCursor(result.data.cursor);
        setLoaded(true);
      } catch {
        if (generation === request.current) setError('Unable to load returns.');
      }
    },
    [api],
  );
  useEffect(() => {
    void load();
    return () => {
      request.current++;
    };
  }, [load]);
  async function run(work: () => Promise<void>) {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError('');
    try {
      await work();
    } catch {
      setError('Request failed. Reload the return before retrying.');
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  function select(data: BuyerReturnDetail) {
    setDetail(data);
    setNote('');
    setBatchId('');
    setConfirmed(false);
    setRefund(data.item.refundState);
    setItems((old) => [data.item, ...old.filter((r) => r.returnId !== data.item.returnId)]);
  }
  const openDetail = (id: string) =>
    run(async () => {
      const result = await api.get(id);
      if (result.ok) select(result.data);
      else setError(failureMessage(result));
    });
  const mutate = (action: BuyerReturnAction) =>
    run(async () => {
      if (!detail || !note.trim()) return;
      const result = await api.mutate({
        returnId: detail.item.returnId,
        revision: detail.item.revision,
        action,
        note,
        batchId,
        refundState: refund,
      });
      if (result.ok) select(result.data);
      else setError(failureMessage(result));
    });
  const item = detail?.item;
  return (
    <section className="tcvs-returns">
      <header className="return-toolbar">
        <h2>Buyer returns</h2>
        <div>
          <button
            type="button"
            onClick={() => void run(() => load())}
            disabled={busy}
            title="Refresh returns"
            aria-label="Refresh returns"
          >
            <RefreshCw size={16} />
          </button>
          <button type="button" onClick={() => setShowNew((v) => !v)} disabled={busy}>
            <Plus size={16} /> Open return
          </button>
        </div>
      </header>
      {error && <p role="alert">{error}</p>}
      {showNew && (
        <form
          className="return-create"
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              const result = await api.open({
                channel,
                orderId,
                lineItemId,
                ...(channel === 'OTHER' ? { cardId } : {}),
                reason,
                ...(reference ? { reference } : {}),
              });
              if (result.ok) {
                select(result.data);
                setShowNew(false);
              } else setError(failureMessage(result));
            });
          }}
        >
          <label>
            Sales channel
            <select
              value={channel}
              disabled={busy}
              onChange={(e) => {
                setChannel(e.target.value as typeof channel);
                setLines([]);
                setLineItemId('');
              }}
            >
              <option value="EBAY_US">eBay</option>
              {admin && <option value="OTHER">Other marketplace / direct sale</option>}
            </select>
          </label>
          <label>
            Order / sale reference
            <input
              required
              maxLength={120}
              value={orderId}
              disabled={busy}
              onChange={(e) => {
                setOrderId(e.target.value);
                setLines([]);
                setLineItemId('');
              }}
            />
          </label>
          {channel === 'EBAY_US' ? (
            <>
              <button
                type="button"
                disabled={busy || !orderId.trim()}
                onClick={() =>
                  void run(async () => {
                    const result = await api.sale(orderId);
                    if (result.ok) setLines(result.data.lines);
                    else setError(failureMessage(result));
                  })
                }
              >
                <Search size={16} /> Find order
              </button>
              <label>
                Order item
                <select
                  required
                  value={lineItemId}
                  disabled={busy || !lines.length}
                  onChange={(e) => setLineItemId(e.target.value)}
                >
                  <option value="">Select item</option>
                  {lines.map((l) => (
                    <option
                      key={l.lineItemId}
                      value={l.lineItemId}
                      disabled={!l.cardId || l.quantity !== 1}
                    >
                      {l.title || l.lineItemId}
                      {!l.cardId ? ' (unlinked)' : l.quantity !== 1 ? ' (multiple quantity)' : ''}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : (
            <>
              <label>
                Sale line reference
                <input
                  required
                  maxLength={120}
                  value={lineItemId}
                  disabled={busy}
                  onChange={(e) => setLineItemId(e.target.value)}
                />
              </label>
              <label>
                TCVS card ID
                <input
                  required
                  maxLength={100}
                  value={cardId}
                  disabled={busy}
                  onChange={(e) => setCardId(e.target.value)}
                />
              </label>
            </>
          )}
          <label>
            Return reason
            <input
              required
              maxLength={500}
              value={reason}
              disabled={busy}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <label>
            {channel === 'OTHER' ? 'Sale evidence reference' : 'eBay return / tracking reference'}
            <input
              required={channel === 'OTHER'}
              maxLength={500}
              value={reference}
              disabled={busy}
              onChange={(e) => setReference(e.target.value)}
            />
          </label>
          <button disabled={busy || !lineItemId} type="submit">
            <Plus size={16} /> Create return
          </button>
          <button type="button" disabled={busy} onClick={() => setShowNew(false)}>
            <X size={16} /> Cancel
          </button>
        </form>
      )}
      <div className="return-layout">
        <div className="return-list">
          <table>
            <thead>
              <tr>
                <th>Card / order</th>
                <th>Physical status</th>
                <th>Refund</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.returnId} aria-selected={item?.returnId === r.returnId}>
                  <td>
                    <button
                      type="button"
                      className="return-title"
                      disabled={busy}
                      onClick={() => void openDetail(r.returnId)}
                    >
                      {r.title}
                    </button>
                    <small>{r.orderId}</small>
                  </td>
                  <td>
                    {label(r.state)}
                    {r.consignmentReviewRequired && <small>Consignment review</small>}
                  </td>
                  <td>{label(r.refundState)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {loaded && !items.length && <p>No buyer returns.</p>}
          {!loaded && !error && <p role="status">Loading returns...</p>}
          {cursor && (
            <button disabled={busy} onClick={() => void run(() => load(cursor))}>
              Load more
            </button>
          )}
        </div>
        {item && (
          <aside className="return-detail" aria-label="Return detail">
            <header className="return-toolbar">
              <h3>{item.title}</h3>
              <button
                disabled={busy}
                title="Refresh return"
                aria-label="Refresh return"
                onClick={() => void openDetail(item.returnId)}
              >
                <RefreshCw size={16} />
              </button>
            </header>
            {detail.imageUrl && (
              <a
                href={`/catalog/${encodeURIComponent(item.cardId)}`}
                target="_blank"
                rel="noreferrer"
              >
                <img className="return-image" src={detail.imageUrl} alt={item.title} />
              </a>
            )}
            <dl>
              <dt>Card</dt>
              <dd>
                <Link to={`/catalog/${item.cardId}`} target="_blank">
                  {item.cardId}
                </Link>
              </dd>
              <dt>Order / line</dt>
              <dd>
                {item.orderId} / {item.lineItemId}
              </dd>
              <dt>Physical status</dt>
              <dd>{label(item.state)}</dd>
              <dt>Refund status</dt>
              <dd>{label(item.refundState)}</dd>
              <dt>Reason</dt>
              <dd>{item.reason}</dd>
              {item.reference && (
                <>
                  <dt>Return reference</dt>
                  <dd>{item.reference}</dd>
                </>
              )}
              {item.holdingLocation && (
                <>
                  <dt>Holding location</dt>
                  <dd>{item.holdingLocation}</dd>
                </>
              )}
              {item.inspectionNotes && (
                <>
                  <dt>Inspection</dt>
                  <dd>{item.inspectionNotes}</dd>
                </>
              )}
              {item.restockedBatchId && (
                <>
                  <dt>Restocked batch</dt>
                  <dd>
                    <Link
                      to={`/storage/batches/${encodeURIComponent(item.restockedBatchId)}`}
                      target="_blank"
                    >
                      {item.restockedBatchId.slice(-6)}
                    </Link>
                  </dd>
                </>
              )}
              {item.consignmentReviewRequired && (
                <>
                  <dt>Consignment</dt>
                  <dd>Payment held for return review</dd>
                </>
              )}
            </dl>
            <label>
              Evidence / inspection notes / holding location
              <textarea
                maxLength={500}
                value={note}
                disabled={busy}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
            {item.state === 'READY_TO_RESTOCK' && (
              <label>
                Destination batch ID
                <input
                  value={batchId}
                  disabled={busy}
                  onChange={(e) => setBatchId(e.target.value)}
                />
              </label>
            )}
            {!['RESTOCKED', 'CLOSED_NO_RETURN'].includes(item.state) && (
              <label className="return-confirm">
                <input
                  type="checkbox"
                  checked={confirmed}
                  disabled={busy}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />{' '}
                Physical card and action confirmed
              </label>
            )}
            <div className="return-actions">
              {(
                [
                  ['RECEIVE', 'Receive card', PackageCheck],
                  ['ACCEPT_INSPECTION', 'Accept inspection', Check],
                  ['HOLD_INSPECTION', 'Hold for investigation', Pause],
                  ['RESTOCK', 'Restock card', Undo2],
                ] as const
              )
                .filter(([action]) => buyerReturnActionAllowed(item.state, action))
                .map(([action, title, Icon]) => (
                  <button
                    key={action}
                    disabled={
                      busy ||
                      !note.trim() ||
                      !confirmed ||
                      (action === 'RESTOCK' && !batchId.trim())
                    }
                    onClick={() => void mutate(action)}
                  >
                    <Icon size={16} /> {title}
                  </button>
                ))}
            </div>
            {admin && (
              <fieldset>
                <legend>Financial review</legend>
                <label>
                  Refund status
                  <select
                    value={refund}
                    disabled={busy}
                    onChange={(e) => setRefund(e.target.value as BuyerRefundState)}
                  >
                    {BUYER_REFUND_STATES.map((s) => (
                      <option key={s} value={s}>
                        {label(s)}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="return-actions">
                  <button
                    disabled={busy || !note.trim()}
                    onClick={() => void mutate('RECORD_REFUND')}
                  >
                    Record refund status
                  </button>
                  {item.state === 'AWAITING_RETURN' && (
                    <button
                      disabled={busy || !note.trim() || !confirmed}
                      onClick={() => void mutate('CLOSE_NO_RETURN')}
                    >
                      Close without physical return
                    </button>
                  )}
                  {item.consignmentReviewRequired &&
                    buyerReturnActionAllowed(item.state, 'REVIEW_FINANCE') && (
                      <button
                        disabled={
                          busy ||
                          !note.trim() ||
                          ['NOT_RECORDED', 'PENDING'].includes(item.refundState)
                        }
                        onClick={() => void mutate('REVIEW_FINANCE')}
                      >
                        Confirm financial review
                      </button>
                    )}
                </div>
              </fieldset>
            )}
            <h4>Recent history</h4>
            <ol className="return-history">
              {detail.events.map((e) => (
                <li key={e.revision}>
                  <strong>{label(e.action)}</strong> {new Date(e.createdAt).toLocaleString()}
                  <div>{e.note}</div>
                </li>
              ))}
            </ol>
          </aside>
        )}
      </div>
    </section>
  );
}
